import { trackEvent } from '@grnhse/datacoral_tracker';

/**
 * Centralized Snowplow analytics tracking for the Talent Matching surfaces
 * (App Review, Past Candidates, and — later — MyGreenhouse Discovery). See
 * GREEN-69176.
 *
 * This module is the single place the event taxonomy lives. Event names and
 * payload shape are PROVISIONAL pending the data team, so keep all shape
 * decisions here — wiring elsewhere should only call these wrappers.
 *
 * Two events, matching the data team's requested split:
 * - `talent_matching_application_view` — one event each time an application is
 *   viewed (e.g. a side panel opening), across every surface.
 * - `talent_matching_user_action` — one event per (application, action) for
 *   everything else: row/bulk/panel actions plus interactions (filters,
 *   pagination, navigation). Bulk actions fan out to one event per application.
 *
 * Conventions:
 * - Every emit is fire-and-forget and wrapped in try/catch, so a tracking
 *   failure can never break the user action.
 * - Attribution (source + org/user/hiring_plan) and `occurred_at` ride on every
 *   event so each surface is distinguishable and time-orderable downstream.
 *   (Snowplow also stamps authoritative server timestamps automatically.)
 * - Payload keys are snake_case (what the warehouse queries on); the function
 *   API is camelCase (TS convention) and mapped to snake_case here.
 * - No Iglu schema is passed yet, so events land under the tracker's default
 *   `generic-event` schema (consistent with existing TM events). A dedicated
 *   schema + custom context can be introduced later without touching callers.
 */

export const TALENT_MATCHING_USER_ACTION = 'talent_matching_user_action';
export const TALENT_MATCHING_APPLICATION_VIEW =
  'talent_matching_application_view';

// Where the event came from. `tm_app_review` is App Review *inside* Talent
// Matching — distinct from the standalone (legacy) App Review page, which is
// not instrumented today; if it ever is, it would emit its own source value.
export type TalentMatchingSource =
  | 'tm_app_review'
  | 'past_candidates'
  | 'mygreenhouse_discovery';

type TalentMatchingActionCategory = 'action' | 'interaction';

export type TalentMatchingAction =
  | 'advance'
  | 'reject'
  | 'reject_as_spam'
  | 'leave_feedback'
  | 'send_email'
  | 'tag'
  | 'fraud_report'
  | 'transfer_to_job'
  | 'navigate_to_profile'
  | 'add_as_prospect'
  | 'add_to_job'
  | 'override_match_strength'
  | 'save_for_later'
  | 'calibration_saved'
  // Configuration Assistant "Apply" — may save a calibration, apply filters,
  // or both (metadata flags say which; the accepted filter set rides the
  // standard `filters` key). Does not also emit calibration_saved.
  | 'assistant_setup_applied';

type TalentMatchingInteraction =
  | 'filters_applied'
  // Past Candidates only: the user restored the suggested/default filter set
  // (not a generic reset) — see RediscoveryPageContent#handleReset.
  | 'default_filters_restored'
  | 'filters_cleared'
  | 'filters_loaded'
  | 'filters_saved'
  | 'paginate'
  | 'ai_summary_click'
  // Configuration Assistant: a user-initiated chat send (typed or the
  // start-setup quick reply); the hidden auto-draft on open is not tracked.
  | 'assistant_message_sent';

// One applied filter and the value(s) it was set to.
export type TrackedFilter = { name: string; values: unknown };

// A filter value that means "not set" — shared by every event that carries a
// `filters` list so filter payloads stay comparable across events.
function isEmptyFilterValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    value === false ||
    (Array.isArray(value) && value.length === 0)
  );
}

// Builds `filters` payload entries from a key/value record.
export function trackedFilterEntries(
  record: Record<string, unknown>,
  excludedKeys: string[] = []
): TrackedFilter[] {
  return Object.entries(record)
    .filter(
      ([name, values]) =>
        !excludedKeys.includes(name) && !isEmptyFilterValue(values)
    )
    .map(([name, values]) => ({ name, values }));
}

export type TalentMatchingAttribution = {
  source: TalentMatchingSource;
  // Required: every event must be attributable to an org + user.
  organizationId: number;
  userId: number;
  hiringPlanId?: number | null;
  // Nullable join key — set when the action happened inside an App Review
  // session (app_review surface); null on surfaces without one.
  appReviewSessionId?: number | null;
  // ISO-8601. Defaults to emit time. For a bulk action, pass one shared value
  // across all per-application events so they can be grouped by it.
  occurredAt?: string;
};

type EntityFields = {
  applicationId?: number | null;
  candidateId?: number | null;
  targetHiringPlanId?: number | null;
  isSameJob?: boolean | null;
  prospectId?: number | null;
  // Shared client-generated UUID correlating a bulk action's per-application
  // events — the canonical grouping key for a bulk action (not a database id).
  bulkActionUuid?: string | null;
  // Total application count in the bulk action.
  bulkActionSize?: number | null;
  // For filter interactions: the applied filters as [{ name, values }].
  filters?: TrackedFilter[] | null;
  metadata?: Record<string, unknown> | null;
};

type TrackUserActionParams = TalentMatchingAttribution &
  EntityFields & {
    category: TalentMatchingActionCategory;
    action: TalentMatchingAction | TalentMatchingInteraction;
  };

type TrackApplicationViewParams = TalentMatchingAttribution & {
  applicationId: number;
  candidateId?: number | null;
};

// Drop keys whose value is `undefined` so we don't emit noise. `null` is kept
// (an explicit "known to be absent").
function compact<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>;
}

function attributionPayload(attribution: TalentMatchingAttribution) {
  return {
    source: attribution.source,
    organization_id: attribution.organizationId,
    user_id: attribution.userId,
    hiring_plan_id: attribution.hiringPlanId,
    app_review_session_id: attribution.appReviewSessionId,
    occurred_at: attribution.occurredAt ?? new Date().toISOString(),
  };
}

export function trackUserAction(params: TrackUserActionParams): void {
  try {
    trackEvent(
      TALENT_MATCHING_USER_ACTION,
      compact({
        ...attributionPayload(params),
        category: params.category,
        action: params.action,
        application_id: params.applicationId,
        candidate_id: params.candidateId,
        target_hiring_plan_id: params.targetHiringPlanId,
        is_same_job: params.isSameJob,
        prospect_id: params.prospectId,
        bulk_action_uuid: params.bulkActionUuid,
        bulk_action_size: params.bulkActionSize,
        filters: params.filters,
        metadata: params.metadata,
      })
    );
  } catch {
    // Analytics must never break the user action.
  }
}

export function trackApplicationView(params: TrackApplicationViewParams): void {
  try {
    trackEvent(
      TALENT_MATCHING_APPLICATION_VIEW,
      compact({
        ...attributionPayload(params),
        application_id: params.applicationId,
        candidate_id: params.candidateId,
      })
    );
  } catch {
    // Analytics must never break the user action.
  }
}
